import { Link } from 'react-router-dom';
import { Globe, Phone, Mail, MapPin, Facebook, Twitter, Linkedin, Instagram, ArrowRight } from 'lucide-react';
import { useContactChannel } from '@/hooks/useContactChannel';

const footerLinks = {
  services: [
    { label: 'All Services Overview', path: '/services' },
    { label: 'International Manpower Recruitment', path: '/international-manpower-recruitment-europe-russia-israel-saudi-arabia' },
    { label: 'Russia Recruitment Agency', path: '/russia-recruitment-agency-india' },
    { label: 'Poland Recruitment Agency', path: '/poland-recruitment-agency-india' },
    { label: 'Eastern Europe Recruitment Hub', path: '/eastern-europe-recruitment-india' },
    { label: 'Work Visa Recruitment', path: '/countries/canada' },
    { label: 'Skilled Migration', path: '/countries/australia' },
    { label: 'Business Placement', path: '/business/usa/eb-5-visa' },
    { label: 'Study Abroad', path: '/study-abroad/' },
    { label: 'Saudi Arabia Visa', path: '/saudi-arabia' },
    // Reinforces silo authority + preserves Success Stories link in footer
    // (replaces the header nav slot which now hosts the Jobs dropdown).
    { label: 'Success Stories', path: '/success-stories' },
  ],
  // Overseas Jobs by Industry — Feb 2026 footer reinforcement of the
  // /jobs silo. Direct hub links so every page on the site has a sitewide
  // crawlable path into each Category Hub (eliminates orphan risk).
  jobsByIndustry: [
    { label: 'All Overseas Jobs (Hub)', path: '/jobs' },
    { label: 'IT & Tech', path: '/jobs/tech-jobs-saudi-arabia' },
    { label: 'Finance & Corporate', path: '/jobs/finance-jobs-saudi-arabia' },
    { label: 'Engineering & Construction', path: '/jobs/engineering-jobs-saudi-arabia' },
    { label: 'Healthcare & Medical', path: '/jobs/healthcare-jobs-saudi-arabia' },
    { label: 'Education & Teaching', path: '/jobs/education-jobs-saudi-arabia' },
  ],
  countries: [
    { label: 'Canada Immigration', path: '/countries/canada' },
    { label: 'Australia PR', path: '/countries/australia' },
    { label: 'Saudi Arabia', path: '/saudi-arabia' },
    { label: 'Saudi Visa Framework 2026', path: '/saudi-visa-framework-2026/' },
    { label: 'UK Skilled Worker', path: '/skilled/uk/uk-skilled-worker-visa' },
    { label: 'Germany Opportunity Card', path: '/germany-opportunity-card' },
    { label: 'UAE Golden Visa', path: '/skilled/uae/uae-golden-visa' },
  ],
  // Study Abroad column — added Feb 2026 to link 14 orphan URLs that GSC
  // flagged as "Crawled but not indexed" due to zero incoming internal links.
  // Mix of 6 country base pages + 8 utility / resource pages.
  studyAbroad: [
    { label: 'Study Abroad Hub', path: '/study-abroad/' },
    { label: 'Study in Canada', path: '/study-abroad/canada/' },
    { label: 'Study in Australia', path: '/study-abroad/australia/' },
    { label: 'Study in UK', path: '/study-abroad/uk/' },
    { label: 'Study in Germany', path: '/study-abroad/germany/' },
    { label: 'Study in Ireland', path: '/study-abroad/ireland/' },
    { label: 'Study in Dubai', path: '/study-abroad/dubai' },
    { label: 'Cost Calculator', path: '/study-abroad/cost-calculator/' },
    { label: 'Free Profile Evaluation', path: '/study-abroad/free-profile-evaluation/' },
    { label: 'Visa Comparison', path: '/study-abroad/visa-comparison/' },
    { label: 'Visa Eligibility Check', path: '/study-abroad/visa-eligibility/' },
    { label: 'Book Counselling', path: '/study-abroad/book-counselling/' },
    { label: 'Country Comparisons', path: '/study-abroad/comparison/' },
    { label: 'Global Partnerships', path: '/study-abroad/global-partnerships/' },
    { label: 'Why Choose ZAFCO', path: '/study-abroad/why-choose-zafco/' },
    { label: 'About ZAFCO Study', path: '/study-abroad/about-zafco/' },
  ],
  quickLinks: [
    { label: 'About Us', path: '/about' },
    { label: 'Our Blogs', path: '/blogs' },
    { label: 'Contact Us', path: '/contact' },
    { label: 'FAQ', path: '/faq-page' },
    { label: 'Privacy Policy', path: '/privacy-policy' },
    { label: 'Study Abroad Privacy Policy', path: '/study-abroad/privacy-policy/' },
    { label: 'Terms & Conditions', path: '/terms-and-conditions' },
  ],
};

const Footer = () => {
  const channel = useContactChannel();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-950 text-white border-t border-white/5" data-testid="footer">
      {/* Main Footer */}
      <div className="container max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10 lg:gap-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="/zafco-logo.png" 
                alt="ZAFCO - International Recruitment Agency" 
                className="h-14 w-auto"
                width="200"
                height="56"
                loading="lazy"
                decoding="async"
              />
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Government of India approved overseas recruitment agency connecting Indian talent 
              with global opportunities across the Gulf, Middle East, Europe, and beyond.
            </p>
            <div className="flex gap-3">
              {[
                { Icon: Facebook,  label: 'Facebook'  },
                { Icon: Twitter,   label: 'Twitter'   },
                { Icon: Linkedin,  label: 'LinkedIn'  },
                { Icon: Instagram, label: 'Instagram' },
              ].map(({ Icon, label }) => (
                <a 
                  key={label}
                  href="#" 
                  aria-label={`ZAFCO on ${label}`}
                  className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:bg-[#C5A028] hover:text-black hover:border-[#C5A028] transition-all"
                  data-testid={`social-link-${label.toLowerCase()}`}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-bold mb-6 font-syne uppercase tracking-wider text-white">Our Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.path}
                    className="text-slate-400 hover:text-[#C5A028] transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Countries */}
          <div>
            <h4 className="text-sm font-bold mb-6 font-syne uppercase tracking-wider text-white">Destinations</h4>
            <ul className="space-y-3">
              {footerLinks.countries.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.path}
                    className="text-slate-400 hover:text-[#C5A028] transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Overseas Jobs by Industry — Feb 2026: silo reinforcement.
              Pushes link equity from every page on the site into the 5
              Category Hubs + the Master Jobs Hub. */}
          <div data-testid="footer-jobs-industry-column">
            <h4 className="text-sm font-bold mb-6 font-syne uppercase tracking-wider text-white">Overseas Jobs</h4>
            <ul className="space-y-3">
              {footerLinks.jobsByIndustry.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.path}
                    className="text-slate-400 hover:text-[#C5A028] transition-colors text-sm"
                    data-testid={`footer-jobs-${link.path.replace(/[^a-z0-9]+/gi, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Study Abroad — added Feb 2026 to surface 14 orphan URLs (GSC cleanup).
              Linked here gives every Study Abroad utility page strong sitewide
              internal links — Google's PageRank can now flow into them. */}
          <div data-testid="footer-study-abroad-column">
            <h4 className="text-sm font-bold mb-6 font-syne uppercase tracking-wider text-white">Study Abroad</h4>
            <ul className="space-y-3">
              {footerLinks.studyAbroad.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.path}
                    className="text-slate-400 hover:text-[#C5A028] transition-colors text-sm"
                    data-testid={`footer-sa-link-${link.path.replace(/[^a-z0-9]+/gi, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-sm font-bold mb-6 font-syne uppercase tracking-wider text-white">Contact Us</h4>
            <div className="space-y-4">
              <a 
                href={channel.whatsappUrl}
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-start gap-3 text-slate-400 hover:text-[#C5A028] transition-colors group"
                data-testid="footer-whatsapp"
                data-vertical={channel.vertical}
              >
                <Phone className="w-4 h-4 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm">{channel.phoneDisplayShort}</p>
                  <p className="text-xs text-slate-400">WhatsApp Available</p>
                </div>
              </a>
              <a 
                href={`mailto:${channel.email}`}
                className="flex items-start gap-3 text-slate-400 hover:text-[#C5A028] transition-colors"
                data-testid="footer-email"
              >
                <Mail className="w-4 h-4 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm">{channel.email}</p>
                  <p className="text-xs text-slate-400">24/7 Support</p>
                </div>
              </a>
              <div className="flex items-start gap-3 text-slate-400">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm">New Delhi, India</p>
                  <p className="text-xs text-slate-400">Head Office</p>
                </div>
              </div>
            </div>

            {/* Government Badge */}
            <div className="mt-6 p-4 bg-white/5 rounded-xl border border-white/10">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Government Approved</p>
              <p className="text-xs text-[#C5A028] font-mono">MEA RC: B-0762/DEL/PER/1000+/5/9148/2015</p>
            </div>
          </div>
        </div>
      </div>

      {/* Legal Compliance Disclaimer — sitewide P0 (Jun 2026)
          Mandatory for Google Ads compliance + immigration-policy auditability.
          Reference: 40-point audit report, Section F, Item 34. */}
      <div className="border-t border-white/5 bg-black/30" data-testid="footer-legal-disclaimer">
        <div className="container max-w-7xl mx-auto px-6 md:px-12 py-5">
          <p className="text-[11.5px] leading-6 text-slate-400">
            <span className="font-semibold text-slate-300">Legal Disclaimer:</span>{' '}
            ZAFCO Human Resource Management is an official, MEA (Ministry of External Affairs, Government of India) approved recruitment firm. We provide professional recruitment, guidance, and documentation support. Please note that final visa issuance, immigration clearance, and employment selection remain solely at the discretion of the respective employers and concerned Government Authorities. ZAFCO does not make deceptive or guaranteed visa/job placement claims.
          </p>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="container max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm">
              © {currentYear} Zafco Human Resource Management. All rights reserved.
            </p>
            <div className="flex flex-wrap gap-6 text-sm">
              {footerLinks.quickLinks.slice(3).map((link) => (
                <Link 
                  key={link.label}
                  to={link.path}
                  className="text-slate-400 hover:text-[#C5A028] transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
